import { useState, useEffect, useRef } from "react";
import { pdfjs } from "react-pdf";
import jsPDF from "jspdf";
import { CaseServices } from "../../services/CaseServices";
import { RpaServices } from "../../services/RpaServices";
import { AuthServices } from "../../services/AuthServices";
import { logOut401, logOut } from "../../config/user";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../assets/crossIcon.svg";
import { CircularProgress } from "@mui/material";
import { WbIncandescentTwoTone } from "@mui/icons-material";
import { handleApiErrorResponses } from "../../utils/ApisErrorHandling";
import { toast } from "react-toastify";
import downIcon from "../../assets/erroneous-down-icon.svg";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

const CaseStageUpdateConfirmation = ({
  setConfirmationPopup,
  updateStageMethodName,
  AllCases,
  getStageCaseReport,
  tmsVersion,
}) => {
  const [cas, setCas] = useState(updateStageMethodName?.case);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [tmsCaseId, setTmsCaseId] = useState(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
  const [highlightedReasonIndex, setHighlightedReasonIndex] = useState(-1);
  const [erroneousDocuments, setErroneousDocuments] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [claimSubmittedAmount, setClaimSubmittedAmount] = useState("");
  const [claimApprovedAmount, setClaimApprovedAmount] = useState("");
  const [erroneousRequestedAmount, setErroneousRequestedAmount] = useState("");
  const [reprocessDocuments, setReprocessDocuments] = useState([]);
  const [uploadingReprocessDocuments, setUploadingReprocessDocuments] =
    useState(false);
  const [reprocessClaimInitiatedAmount, setReprocessClaimInitiatedAmount] =
    useState("");

  const reprocessFileInputRef = useRef(null);

  const erroneousFileInputRef = useRef(null);
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  const localISOTime = new Date(now - tzOffset).toISOString().slice(0, 16);
  const [initiationDate, setInitiationDate] = useState(localISOTime);
  const [reprocessTransactionDate, setReprocessTransactionDate] = useState("");
  const erroneousReasons = ["Full Claim Amount Not Settled", "Other"];
  const reprocessReasons = ["Claim Modified for Reprocess", "Other"];
  const [erroneousTransactionDate, setErroneousTransactionDate] = useState("");
  const processErroneousPdf = async (file) => {
    const pdfUrl = URL.createObjectURL(file);

    try {
      const pdfDocument = await pdfjs.getDocument(pdfUrl).promise;
      const formData = new FormData();

      for (
        let pageNumber = 1;
        pageNumber <= pdfDocument.numPages;
        pageNumber++
      ) {
        const canvas = document.createElement("canvas");
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 3 });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: canvas.getContext("2d"),
          viewport,
        };

        await page.render(renderContext).promise;

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg");
        });

        if (blob) {
          formData.append(
            "images",
            blob,
            `${file.name.replace(/\.pdf$/i, "")}_${pageNumber}.jpeg`,
          );
        }
      }

      const response = await AuthServices.uploadPdfToImage(formData, () => {});

      const urls = response?.data?.urls || [];

      return {
        documentName: file.name,
        documenturlmasters: urls.map((url, index) => ({
          documentUrl: url,
          sequence: String(index + 1),
        })),
      };
    } finally {
      URL.revokeObjectURL(pdfUrl);
    }
  };
  const processReprocessPdf = async (file) => {
    const pdfUrl = URL.createObjectURL(file);

    try {
      const pdfDocument = await pdfjs.getDocument(pdfUrl).promise;
      const formData = new FormData();

      for (
        let pageNumber = 1;
        pageNumber <= pdfDocument.numPages;
        pageNumber++
      ) {
        const canvas = document.createElement("canvas");
        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 3 });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: canvas.getContext("2d"),
          viewport,
        };

        await page.render(renderContext).promise;

        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg");
        });

        if (blob) {
          formData.append(
            "images",
            blob,
            `${file.name.replace(/\.pdf$/i, "")}_${pageNumber}.jpeg`,
          );
        }
      }

      const response = await AuthServices.uploadPdfToImage(formData, () => {});
      const urls = response?.data?.urls || [];

      return {
        documentName: file.name,
        documenturlmasters: urls.map((url, index) => ({
          documentUrl: url,
          sequence: String(index + 1),
        })),
      };
    } finally {
      URL.revokeObjectURL(pdfUrl);
    }
  };
  const handleErroneousDocumentUpload = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const allowedFileTypes = ["application/pdf", "image/png", "image/jpeg"];

    const unsupportedFiles = files.filter(
      (file) => !allowedFileTypes.includes(file.type),
    );

    if (unsupportedFiles.length > 0) {
      window.alert(
        "File not supported. Only PDF, JPEG and PNG files are supported.",
      );
      event.target.value = "";
      return;
    }

    setUploadingDocuments(true);

    try {
      const uploadedDocuments = [];

      for (const file of files) {
        if (file.type === "application/pdf") {
          const documentData = await processErroneousPdf(file);

          uploadedDocuments.push(documentData);
        } else if (file.type === "image/png" || file.type === "image/jpeg") {
          const formData = new FormData();

          formData.append("image", file);

          const response = await AuthServices.uploadImage(formData);

          uploadedDocuments.push({
            documentName: file.name,
            documenturlmasters: [
              {
                documentUrl: response?.data?.url,
                sequence: "1",
              },
            ],
          });
        }
      }

      setErroneousDocuments((previousDocuments) => [
        ...previousDocuments,
        ...uploadedDocuments,
      ]);
    } catch (error) {
      if (
        error?.name === "PasswordException" ||
        error?.code === pdfjs.PasswordResponses?.NEED_PASSWORD ||
        error?.code === pdfjs.PasswordResponses?.INCORRECT_PASSWORD
      ) {
        window.alert("Password-protected PDF files are not supported.");
      } else {
        handleApiErrorResponses(error);
      }
    } finally {
      setUploadingDocuments(false);
      event.target.value = "";
    }
  };
  const handleReprocessDocumentUpload = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    const allowedFileTypes = ["application/pdf", "image/png", "image/jpeg"];

    const unsupportedFiles = files.filter(
      (file) => !allowedFileTypes.includes(file.type),
    );

    if (unsupportedFiles.length > 0) {
      window.alert(
        "File not supported. Only PDF, JPEG and PNG files are supported.",
      );
      event.target.value = "";
      return;
    }

    setUploadingReprocessDocuments(true);

    try {
      const uploadedDocuments = [];

      for (const file of files) {
        if (file.type === "application/pdf") {
          const documentData = await processReprocessPdf(file);

          uploadedDocuments.push(documentData);
        } else if (file.type === "image/png" || file.type === "image/jpeg") {
          const formData = new FormData();

          formData.append("image", file);

          const response = await AuthServices.uploadImage(formData);

          uploadedDocuments.push({
            documentName: file.name,
            documenturlmasters: [
              {
                documentUrl: response?.data?.url,
                sequence: "1",
              },
            ],
          });
        }
      }

      setReprocessDocuments((previousDocuments) => [
        ...previousDocuments,
        ...uploadedDocuments,
      ]);
    } catch (error) {
      if (
        error?.name === "PasswordException" ||
        error?.code === pdfjs.PasswordResponses?.NEED_PASSWORD ||
        error?.code === pdfjs.PasswordResponses?.INCORRECT_PASSWORD
      ) {
        window.alert("Password-protected PDF files are not supported.");
      } else {
        handleApiErrorResponses(error);
      }
    } finally {
      setUploadingReprocessDocuments(false);
      event.target.value = "";
    }
  };
  const removeReprocessDocument = (index) => {
    setReprocessDocuments((previousDocuments) =>
      previousDocuments.filter((_, documentIndex) => documentIndex !== index),
    );
  };
  const removeErroneousDocument = (index) => {
    setErroneousDocuments((previousDocuments) =>
      previousDocuments.filter((_, documentIndex) => documentIndex !== index),
    );
  };
  const handleDownloadErroneousDocument = async (document) => {
    try {
      const documentMasters = document?.documenturlmasters || [];

      if (!documentMasters.length) {
        window.alert("Document is not available for download");
        return;
      }

      const validDocuments = documentMasters
        .filter((master) => master?.documentUrl)
        .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));

      if (!validDocuments.length) {
        window.alert("Document is not available for download");
        return;
      }

      // If the original document is a PDF,
      // always create one PDF containing all pages.
      const isPdf = document?.documentName?.toLowerCase().endsWith(".pdf");

      // For non-PDF single images, download the image directly.
      if (!isPdf && validDocuments.length === 1) {
        const master = validDocuments[0];

        const response = await fetch(master.documentUrl);

        if (!response.ok) {
          throw new Error("Failed to download document");
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = window.document.createElement("a");
        link.href = blobUrl;

        const extension = blob.type === "image/png" ? "png" : "jpeg";

        link.download = `${document.documentName.replace(
          /\.[^/.]+$/,
          "",
        )}.${extension}`;

        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);

        return;
      }

      // Create a single PDF for PDF documents
      // and for multiple-page documents.
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      for (let index = 0; index < validDocuments.length; index++) {
        const master = validDocuments[index];

        const response = await fetch(master.documentUrl);

        if (!response.ok) {
          throw new Error("Failed to download document page");
        }

        const blob = await response.blob();

        const imageUrl = window.URL.createObjectURL(blob);

        const image = new Image();

        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = imageUrl;
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imageRatio = image.width / image.height;
        const pageRatio = pageWidth / pageHeight;

        let imageWidth;
        let imageHeight;

        if (imageRatio > pageRatio) {
          imageWidth = pageWidth;
          imageHeight = pageWidth / imageRatio;
        } else {
          imageHeight = pageHeight;
          imageWidth = pageHeight * imageRatio;
        }

        const x = (pageWidth - imageWidth) / 2;
        const y = (pageHeight - imageHeight) / 2;

        if (index > 0) {
          pdf.addPage();
        }

        const imageFormat = blob.type === "image/png" ? "PNG" : "JPEG";

        pdf.addImage(image, imageFormat, x, y, imageWidth, imageHeight);

        window.URL.revokeObjectURL(imageUrl);
      }

      const fileName = `${document.documentName.replace(/\.[^/.]+$/, "")}.pdf`;

      pdf.save(fileName);
    } catch (error) {
      console.error("Error downloading document:", error);

      window.alert("Unable to download document");
    }
  };
  const updateStageMethods = {
    handleInitiateAdmission: async () => {
      setSubmitting(true);
      try {
        const res = await CaseServices.initiateAdmission(cas?.id);
        toast.success("Admission Initiated");
        setConfirmationPopup(false);
        await AllCases();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setSubmitting(false);
    },
    handleAdmissionCancelledByHospital: async () => {
      setSubmitting(true);
      const postStageData = {
        hsaStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Admision Cancelled"
          : "",
        commandCeneterStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Admission Cancelled By Hospital"
          : "",
        nextStepUser: "NA",
        caseDetails: [
          {
            query: "",
            queryResponse: "",
          },
        ],
      };
      try {
        const stageResponse = await CaseServices.updateSubMasterStage(
          cas.id,
          postStageData,
        );
        window.alert(stageResponse.data.message);
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setSubmitting(false);
    },
    handleInitiateOnTMS: async () => {
      setSubmitting(true);
      const postStageData = {
        hsaStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Admission Initiated on TMS"
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("enhancement")
            ? "Enhancement Initiated on TMS"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("claim")
              ? "Claim Initiated on TMS"
              : "",
        commandCeneterStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("query")
          ? cas?.submasterstagestatuses[0]?.commandCenterStage
              ?.toLowerCase()
              .includes("admission")
            ? "Admission Query Reply Initiated on TMS"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("enhancement")
              ? "Enhancement Query Reply Initiated on TMS"
              : cas?.submasterstagestatuses[0]?.commandCenterStage
                    ?.toLowerCase()
                    .includes("claim")
                ? "Claim Query Reply Initiated on TMS"
                : ""
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("admission")
            ? "Admission Initiated on TMS"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("enhancement")
              ? "Enhancement Initiated on TMS"
              : cas?.submasterstagestatuses[0]?.commandCenterStage
                    ?.toLowerCase()
                    .includes("claim")
                ? "Claim Initiated on TMS"
                : "",
        nextStepUser: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Internal team C"
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("enhancement")
            ? "Internal Team E"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("claim")
              ? "Internal Team E"
              : "",
        initiationDate: new Date(initiationDate).toISOString(),
        caseDetails: [
          {
            query: "",
            queryResponse: "",
          },
        ],
      };
      try {
        if (cas?.hospital?.tmsVersion === "1.0") {
          if (cas?.pmjayId || tmsCaseId) {
            await RpaServices.addRpaRequest({
              hospitalId: cas.hospitalId,
              caseStatus: "STATUS_UPDATE",
              requestType: cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("claim")
                ? "CLAIM"
                : "PREAUTH",
              caseId: cas.id,
              pmjayCaseId: cas?.pmjayId || tmsCaseId,
            });
            const stageResponse = await CaseServices.updateSubMasterStage(
              cas.id,
              postStageData,
            );
            window.alert(stageResponse.data.message);
          } else {
            window.alert("Provide Pmjay Case Id");
          }
        } else {
          if (cas?.pmjayRegistrationId) {
            await RpaServices.addRpaRequest({
              hospitalId: cas.hospitalId,
              caseStatus: "STATUS_UPDATE",
              requestType: cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("claim")
                ? "CLAIM"
                : "PREAUTH",
              caseId: cas.id,
              pmjayRegistrationId: cas?.pmjayRegistrationId,
            });
            const stageResponse = await CaseServices.updateSubMasterStage(
              cas.id,
              postStageData,
            );
            window.alert(stageResponse.data.message);
          } else {
            window.alert("RegistrationId is missing");
          }
        }
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setSubmitting(false);
    },
    handleApprovedOnTMS: async () => {
      setSubmitting(true);
      const postStageData = {
        hsaStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Admission Approved on TMS"
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("enhancement")
            ? "Enhancement Approved on TMS"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("claim")
              ? "Claim Approved on TMS"
              : "",
        commandCeneterStage: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Admission Approved on TMS"
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("enhancement")
            ? "Enhancement Approved on TMS"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("claim")
              ? "Claim Approved on TMS"
              : "",
        nextStepUser: cas?.submasterstagestatuses[0]?.commandCenterStage
          ?.toLowerCase()
          .includes("admission")
          ? "Hospital Sub Admin (HSA)"
          : cas?.submasterstagestatuses[0]?.commandCenterStage
                ?.toLowerCase()
                .includes("enhancement")
            ? "Hospital Sub Admin (HSA)"
            : cas?.submasterstagestatuses[0]?.commandCenterStage
                  ?.toLowerCase()
                  .includes("claim")
              ? "Internal Team F"
              : "",
        caseDetails: [
          {
            query: "",
            queryResponse: "",
          },
        ],
      };
      try {
        const stageResponse = await CaseServices.updateSubMasterStage(
          cas.id,
          postStageData,
        );
        window.alert(stageResponse.data.message);
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setSubmitting(false);
    },
    handleClaimSentToBank: async () => {
      setSubmitting(true);
      const postStageData = {
        hsaStage: "Claim Sent To Bank ",
        commandCeneterStage: "Claim Sent To Bank on TMS",
        nextStepUser: "Hospital Sub Admin (HSA)",
        caseDetails: [
          {
            query: "",
            queryResponse: "",
          },
        ],
      };
      try {
        const stageResponse = await CaseServices.updateSubMasterStage(
          cas.id,
          postStageData,
        );
        window.alert(stageResponse.data.message);
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }
      setSubmitting(false);
    },
    handleErroneousRaise: async () => {
      if (!selectedReason) {
        window.alert("Please Select Reason");
        return;
      }

      if (erroneousDocuments.length === 0) {
        window.alert("Please Upload Document");
        return;
      }

      const hasInvalidDocument = erroneousDocuments.some(
        (document) =>
          !document.documentName ||
          !document.documenturlmasters?.length ||
          document.documenturlmasters.some((master) => !master.documentUrl),
      );

      if (hasInvalidDocument) {
        window.alert("Document upload is incomplete");
        return;
      }

      setSubmitting(true);

      const postStageData = {
        caseId: cas.id,
        documents: erroneousDocuments.map((document) => ({
          caseId: cas.id,
          documentType: "ONDISCHARGEDOCUMENT",
          documentName: document.documentName,
          status: "INREVIEW",
          documenturlmasters: document.documenturlmasters.map((master) => ({
            documentUrl: master.documentUrl,
            sequence: master.sequence,
          })),
        })),
        reason: selectedReason,
        isNewDocMapped: true,
      };

      try {
        const response =
          await CaseServices.initiateErroneousClaim(postStageData);

        console.log("Erroneous Claim Response:", response);

        setConfirmationPopup(false);

        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        console.error("Erroneous Claim API Error:", error);

        handleApiErrorResponses(error);
      } finally {
        setSubmitting(false);
      }
    },
    handleRaiseErroneousConfirmation: async () => {
      setSubmitting(true);

      const postStageData = {
        caseId: cas.id,
      };

      try {
        const response =
          await CaseServices.initiateErroneousClaim(postStageData);

        window.alert(response.data.message);
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }

      setSubmitting(false);
    },
    handleReprocessRaise: async () => {
      if (!selectedReason) {
        window.alert("Please Select Reason");
        return;
      }

      if (reprocessDocuments.length === 0) {
        window.alert("Please Upload Document");
        return;
      }

      const hasInvalidDocument = reprocessDocuments.some(
        (document) =>
          !document.documentName ||
          !document.documenturlmasters?.length ||
          document.documenturlmasters.some((master) => !master.documentUrl),
      );

      if (hasInvalidDocument) {
        window.alert("Document upload is incomplete");
        return;
      }

      setSubmitting(true);

      const postStageData = {
        caseId: cas.id,
        documents: reprocessDocuments.map((document) => ({
          caseId: cas.id,
          documentType: "ONDISCHARGEDOCUMENT",
          documentName: document.documentName,
          status: "INREVIEW",
          documenturlmasters: document.documenturlmasters.map((master) => ({
            documentUrl: master.documentUrl,
            sequence: master.sequence,
          })),
        })),
        reason: selectedReason,
        isNewDocMapped: true,
      };

      try {
        const response =
          await CaseServices.initiateReprocessClaim(postStageData);

        console.log("Reprocess Claim Response:", response);

        setConfirmationPopup(false);

        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        console.error("Reprocess Claim API Error:", error);

        handleApiErrorResponses(error);
      } finally {
        setSubmitting(false);
      }
    },
    handleInitiateErroneous: async () => {
      if (!claimSubmittedAmount) {
        window.alert("Please Enter Claim Submitted Amount");
        return;
      }

      if (!claimApprovedAmount) {
        window.alert("Please Enter Claim Approved Amount");
        return;
      }

      if (!erroneousRequestedAmount) {
        window.alert("Please Enter Erroneous Requested Amount");
        return;
      }

      if (!cas?.pmjayRegistrationId) {
        window.alert("PMJAY Registration ID is missing");
        return;
      }

      if (!erroneousTransactionDate) {
        window.alert("Please Select Transaction Date");
        return;
      }

      setSubmitting(true);

      try {
        const formData = new FormData();

        formData.append("claimSubmittedAmount", claimSubmittedAmount);
        formData.append("claimApprovedAmount", claimApprovedAmount);
        formData.append("erroneousRequestedAmount", erroneousRequestedAmount);
        formData.append(
          "transactionDate",
          new Date(`${erroneousTransactionDate}T00:00:00`).toISOString(),
        );
        formData.append("pmjayStatus", "Erroneous Claim Requested");

        const response = await CaseServices.updateErroneousClaim(
          cas?.pmjayRegistrationId,
          formData,
        );

        console.log("Update Erroneous Claim Response:", response);

        setConfirmationPopup(false);

        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        console.error("Update Erroneous Claim API Error:", error);

        handleApiErrorResponses(error);
      } finally {
        setSubmitting(false);
      }
    },
    handleRaiseReprocessConfirmation: async () => {
      setSubmitting(true);

      const postStageData = {
        caseId: cas.id,
      };

      try {
        const response =
          await CaseServices.initiateReprocessClaim(postStageData);

        window.alert(response.data.message);
        setConfirmationPopup(false);
        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        handleApiErrorResponses(error);
      }

      setSubmitting(false);
    },
    handleInitiateReprocess: async () => {
      if (!reprocessClaimInitiatedAmount) {
        window.alert("Please Enter Claim Initiated Amount");
        return;
      }

      if (!reprocessTransactionDate) {
        window.alert("Please Select Transaction Date");
        return;
      }

      if (!cas?.pmjayRegistrationId) {
        window.alert("PMJAY Registration ID is missing");
        return;
      }

      setSubmitting(true);

      try {
        const formData = new FormData();

        formData.append("claimSubmittedAmount", reprocessClaimInitiatedAmount);

        formData.append(
          "transactionDate",
          new Date(`${reprocessTransactionDate}T00:00:00`).toISOString(),
        );

        formData.append("pmjayStatus", "Reprocess Requested");

        const response = await CaseServices.updateReprocessClaim(
          cas?.pmjayRegistrationId,
          formData,
        );

        console.log("Update Reprocess Claim Response:", response);

        setConfirmationPopup(false);

        await AllCases();
        await getStageCaseReport();
      } catch (error) {
        console.error("Update Reprocess Claim API Error:", error);

        handleApiErrorResponses(error);
      } finally {
        setSubmitting(false);
      }
    },
  };
  const handleTMSCaseIdSubmit = async () => {
    setSubmitting(true);
    if (tmsCaseId) {
      try {
        const response = CaseServices.updateCase(
          {
            pmjayId: tmsCaseId,
          },
          cas.id,
        );
        // console.log("tms id update response:", response.data);
        await updateStageMethods[updateStageMethodName?.method]();
      } catch (error) {
        handleApiErrorResponses(error);
      }
    } else {
      window.alert("Please Enter TMS Case ID before submitting.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed top-0 left-0 bg-[#017C5780] w-screen h-screen flex justify-center items-center z-[1200]">
      <div
        className={`relative bg-white flex flex-col ${
          updateStageMethodName?.method
            ?.toLowerCase()
            ?.includes("handleinitiateerroneous")
            ? "w-[1000px] h-[420px] rounded-[32px]"
            : updateStageMethodName?.method
                  ?.toLowerCase()
                  ?.includes("handleinitiatereprocess")
              ? "w-[1000px] h-[380px] rounded-[32px]"
              : updateStageMethodName?.method
                    ?.toLowerCase()
                    ?.includes("handleerroneousraises")
                ? "w-[80%] max-w-[1400px] min-h-[560px] rounded-[24px]"
                : updateStageMethodName?.method
                      ?.toLowerCase()
                      ?.includes("handlereprocessraises")
                  ? "w-[80%] max-w-[1400px] min-h-[560px] rounded-[24px]"
                  : "h-[40%] w-[25%] justify-center items-center rounded-[24px]"
        }`}
      >
        <div
          className="absolute top-8 right-8 cursor-pointer z-10"
          onClick={() => setConfirmationPopup(false)}
        >
          <img src={CloseIcon} alt="close-icon" className="w-7 h-7" />
        </div>
        {updateStageMethodName?.method
          ?.toLowerCase()
          ?.includes("handleinitiateontms") &&
        !cas?.pmjayId &&
        cas?.hospital?.tmsVersion === "1.0" ? (
          <div className="flex items-center gap-3 w-[90%]">
            {/* <div className="text-xl font-medium">
              Please enter TMS Case Id to initiate admission on TMS.
            </div> */}
            <div
              style={{ border: "1px solid #E4E4E4" }}
              className="p-2 rounded-[8px] w-[80%]"
            >
              <input
                type="text"
                value={tmsCaseId}
                placeholder="Enter TMS Case Id"
                className="w-full outline-none border-none"
                onChange={(e) => setTmsCaseId(e.target.value)}
              />
            </div>
            {submitting ? (
              <CircularProgress />
            ) : (
              <div
                className="text-sm text-white bg-[#017C57] font-semibold px-8 py-2 rounded-full cursor-pointer"
                onClick={handleTMSCaseIdSubmit}
              >
                SUBMIT
              </div>
            )}
          </div>
        ) : updateStageMethodName?.method
            ?.toLowerCase()
            ?.includes("handleerroneousraises") ? (
          <div className="w-full px-10 py-8">
            {/* Header */}
            <div className="relative flex items-center justify-center">
              <div
                className="text-[24px] font-normal leading-[100%] text-center text-[#191919]"
                style={{ fontFamily: "Lufga" }}
              >
                Raise Erroneous Claim
              </div>
            </div>

            {/* Divider */}
            <div
              className="w-full mt-[30px]"
              style={{
                borderTop: "1px solid #E8E8E8",
              }}
            />

            {/* Upload Section */}
            <div className="mt-[30px] flex items-start justify-between">
              {" "}
              {/* Upload Button + Supported Files */}
              <div className="flex flex-col items-start">
                <div
                  className="w-[220px] h-[50px] flex items-center justify-center gap-[8px] rounded-[31.87px] cursor-pointer"
                  style={{
                    border: "1px solid #00875A",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                  }}
                  onClick={() => erroneousFileInputRef.current?.click()}
                >
                  <FileUploadOutlinedIcon
                    style={{
                      fontSize: 24,
                      color: "#191919",
                    }}
                  />

                  <span
                    className="text-[16px] font-normal leading-[100%] text-[#4A4A4A]"
                    style={{ fontFamily: "Lufga" }}
                  >
                    Upload Document
                  </span>
                </div>

                <input
                  ref={erroneousFileInputRef}
                  type="file"
                  accept=".png,.pdf,.jpeg"
                  multiple
                  hidden
                  onChange={handleErroneousDocumentUpload}
                />

                <div
                  className="mt-[8px] ml-[40px] text-[12px] font-normal leading-[100%] text-[#555555]"
                  style={{ fontFamily: "Lufga" }}
                >
                  File Supported : .png, .pdf, .jpeg
                </div>
              </div>
              {/* Uploaded Documents */}
              <div className="flex items-start gap-5">
                {uploadingDocuments && (
                  <div className="flex items-center justify-center w-[195px] h-[125px]">
                    <CircularProgress size={30} />
                  </div>
                )}

                {!uploadingDocuments && erroneousDocuments.length > 0 && (
                  <div className="flex items-start gap-5 flex-wrap justify-end">
                    {erroneousDocuments.map((document, index) => (
                      <div
                        key={`${document.documentName}-${index}`}
                        className="relative w-[195px] h-[125px] border border-[#E4E4E4] rounded-[12px] bg-white overflow-visible"
                      >
                        {/* Preview */}
                        <div className="w-full h-[92px] rounded-t-[12px] overflow-hidden bg-[#F7F7F7]">
                          {document.documenturlmasters?.[0]?.documentUrl ? (
                            <img
                              src={document.documenturlmasters[0].documentUrl}
                              alt={document.documentName}
                              className="w-full h-full object-contain"
                            />
                          ) : null}
                        </div>

                        {/* Page Count */}
                        <div className="absolute bottom-1 left-2 text-[16px] text-[#191919]">
                          {document.documenturlmasters?.length || 0} Pages
                        </div>

                        {/* Open / Preview Icon */}
                        {/* Download Document Icon */}
                        <div
                          className="absolute bottom-1 right-2 text-[#191919] text-[25px] cursor-pointer"
                          onClick={() =>
                            handleDownloadErroneousDocument(document)
                          }
                        >
                          ↗
                        </div>

                        {/* Remove */}
                        <div
                          className="absolute -right-[18px] -top-[18px] w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center cursor-pointer"
                          style={{ border: "1px solid #FFD0D0" }}
                          onClick={() => removeErroneousDocument(index)}
                        >
                          <span className="text-[#FF5C5C] text-[25px] leading-none">
                            ×
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Reason Dropdown */}
            {/* Reason Dropdown */}
            <div className="relative mt-[30px] mb-[135px]">
              <div
                className="w-full h-[54px] rounded-full px-6 flex items-center justify-between cursor-pointer"
                style={{
                  border: "1px solid #0000001A",
                }}
                tabIndex={0}
                role="combobox"
                aria-expanded={reasonDropdownOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setReasonDropdownOpen((previous) => {
                    const nextState = !previous;

                    if (nextState) {
                      const selectedIndex =
                        erroneousReasons.indexOf(selectedReason);
                      setHighlightedReasonIndex(
                        selectedIndex >= 0 ? selectedIndex : 0,
                      );
                    }

                    return nextState;
                  });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      const selectedIndex =
                        erroneousReasons.indexOf(selectedReason);

                      setHighlightedReasonIndex(
                        selectedIndex >= 0 ? selectedIndex : 0,
                      );
                      setReasonDropdownOpen(true);
                    } else if (highlightedReasonIndex >= 0) {
                      setSelectedReason(
                        erroneousReasons[highlightedReasonIndex],
                      );
                      setReasonDropdownOpen(false);
                    }

                    return;
                  }

                  if (event.key === "ArrowDown") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      setReasonDropdownOpen(true);
                      setHighlightedReasonIndex(0);
                      return;
                    }

                    setHighlightedReasonIndex((previousIndex) => {
                      if (previousIndex < erroneousReasons.length - 1) {
                        return previousIndex + 1;
                      }

                      return 0;
                    });

                    return;
                  }

                  if (event.key === "ArrowUp") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      setReasonDropdownOpen(true);
                      setHighlightedReasonIndex(erroneousReasons.length - 1);
                      return;
                    }

                    setHighlightedReasonIndex((previousIndex) => {
                      if (previousIndex > 0) {
                        return previousIndex - 1;
                      }

                      return erroneousReasons.length - 1;
                    });

                    return;
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setReasonDropdownOpen(false);
                  }
                }}
              >
                <span
                  className={`text-[16px] leading-[100%] ${
                    selectedReason ? "text-[#191919]" : "text-[#777777]"
                  }`}
                  style={{ fontFamily: "Lufga" }}
                >
                  {selectedReason || "Select Reason"}
                </span>

                <img
                  src={downIcon}
                  alt="dropdown"
                  className="w-[16px] h-[16px] object-contain"
                />
              </div>

              {reasonDropdownOpen && (
                <div
                  className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#E0E0E0] rounded-[16px] shadow-lg overflow-hidden"
                  role="listbox"
                >
                  {erroneousReasons.map((reason, index) => (
                    <div
                      key={reason}
                      role="option"
                      aria-selected={selectedReason === reason}
                      className={`px-7 py-4 text-[18px] text-[#191919] cursor-pointer ${
                        highlightedReasonIndex === index ? "bg-[#F7F7F7]" : ""
                      }`}
                      onMouseEnter={() => setHighlightedReasonIndex(index)}
                      onClick={() => {
                        setSelectedReason(reason);
                        setReasonDropdownOpen(false);
                        setHighlightedReasonIndex(index);
                      }}
                    >
                      {reason}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-[30px]">
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                <div
                  className="w-[179px] h-[54px] flex items-center justify-center rounded-[100px] cursor-pointer"
                  style={{
                    border: "1px solid #00875A",
                    paddingTop: "16px",
                    paddingRight: "24px",
                    paddingBottom: "16px",
                    paddingLeft: "24px",
                  }}
                  onClick={() =>
                    updateStageMethods[updateStageMethodName?.method]()
                  }
                >
                  <span
                    className="text-[14px] font-medium leading-[155%] tracking-[-0.07em] text-center text-[#017C57]"
                    style={{ fontFamily: "Lufga" }}
                  >
                    Raise Erroneous Claim
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : updateStageMethodName?.method
            ?.toLowerCase()
            ?.includes("handlereprocessraises") ? (
          <div
            className="w-full h-full flex flex-col"
            style={{ padding: "30px", gap: "30px" }}
          >
            {/* Header */}
            <div className="relative flex items-center justify-center">
              <div
                className="text-[24px] font-normal leading-[100%] text-center text-[#191919]"
                style={{ fontFamily: "Lufga" }}
              >
                Raise Reprocess Claim
              </div>
            </div>

            {/* Divider */}
            <div
              className="w-full"
              style={{
                borderTop: "1px solid #E8E8E8",
              }}
            />

            {/* Upload Section */}
            <div className="flex items-start justify-between gap-10">
              <div className="flex flex-col">
                <div
                  className="w-[220px] h-[50px] flex items-center justify-center gap-[8px] rounded-[31.87px] cursor-pointer"
                  style={{
                    border: "1px solid #00875A",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                  }}
                  onClick={() => reprocessFileInputRef.current?.click()}
                >
                  <FileUploadOutlinedIcon
                    style={{
                      fontSize: 24,
                      color: "#191919",
                    }}
                  />

                  <span
                    className="text-[16px] font-normal leading-[100%] text-[#4A4A4A]"
                    style={{ fontFamily: "Lufga" }}
                  >
                    Upload Document
                  </span>
                </div>

                <input
                  ref={reprocessFileInputRef}
                  type="file"
                  accept=".png,.pdf,.jpeg"
                  multiple
                  hidden
                  onChange={handleReprocessDocumentUpload}
                />

                <div
                  className="mt-[8px] ml-[40px] text-[12px] font-normal leading-[100%] text-[#555555]"
                  style={{ fontFamily: "Lufga" }}
                >
                  File Supported : .png, .pdf, .jpeg
                </div>
              </div>

              {/* Uploaded Documents */}
              <div className="flex items-start gap-5">
                {uploadingReprocessDocuments && (
                  <div className="flex items-center justify-center w-[195px] h-[125px]">
                    <CircularProgress size={30} />
                  </div>
                )}

                {!uploadingReprocessDocuments &&
                  reprocessDocuments.length > 0 && (
                    <div className="flex items-start gap-5 flex-wrap justify-end">
                      {reprocessDocuments.map((document, index) => (
                        <div
                          key={`${document.documentName}-${index}`}
                          className="relative w-[195px] h-[125px] border border-[#E4E4E4] rounded-[12px] bg-white overflow-visible"
                        >
                          <div className="w-full h-[92px] rounded-t-[12px] overflow-hidden bg-[#F7F7F7]">
                            {document.documenturlmasters?.[0]?.documentUrl && (
                              <img
                                src={document.documenturlmasters[0].documentUrl}
                                alt={document.documentName}
                                className="w-full h-full object-contain"
                              />
                            )}
                          </div>

                          <div className="absolute bottom-1 left-2 text-[16px] text-[#191919]">
                            {document.documenturlmasters?.length || 0} Pages
                          </div>

                          <div
                            className="absolute bottom-1 right-2 text-[#191919] text-[25px] cursor-pointer"
                            onClick={() =>
                              handleDownloadErroneousDocument(document)
                            }
                          >
                            ↗
                          </div>

                          <div
                            className="absolute -right-[18px] -top-[18px] w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center cursor-pointer"
                            style={{ border: "1px solid #FFD0D0" }}
                            onClick={() => removeReprocessDocument(index)}
                          >
                            <span className="text-[#FF5C5C] text-[25px] leading-none">
                              ×
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>

            {/* Reason Dropdown */}
            <div className="relative w-full">
              <div
                className="w-full h-[54px] rounded-full px-6 flex items-center justify-between cursor-pointer"
                style={{
                  border: "1px solid #0000001A",
                }}
                tabIndex={0}
                role="combobox"
                aria-expanded={reasonDropdownOpen}
                aria-haspopup="listbox"
                onClick={() => {
                  setReasonDropdownOpen((previous) => {
                    const nextState = !previous;

                    if (nextState) {
                      const selectedIndex =
                        reprocessReasons.indexOf(selectedReason);

                      setHighlightedReasonIndex(
                        selectedIndex >= 0 ? selectedIndex : 0,
                      );
                    }

                    return nextState;
                  });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      const selectedIndex =
                        reprocessReasons.indexOf(selectedReason);

                      setHighlightedReasonIndex(
                        selectedIndex >= 0 ? selectedIndex : 0,
                      );

                      setReasonDropdownOpen(true);
                    } else if (highlightedReasonIndex >= 0) {
                      setSelectedReason(
                        reprocessReasons[highlightedReasonIndex],
                      );
                      setReasonDropdownOpen(false);
                    }

                    return;
                  }

                  if (event.key === "ArrowDown") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      setReasonDropdownOpen(true);
                      setHighlightedReasonIndex(0);
                      return;
                    }

                    setHighlightedReasonIndex((previousIndex) => {
                      if (previousIndex < reprocessReasons.length - 1) {
                        return previousIndex + 1;
                      }

                      return 0;
                    });

                    return;
                  }

                  if (event.key === "ArrowUp") {
                    event.preventDefault();

                    if (!reasonDropdownOpen) {
                      setReasonDropdownOpen(true);
                      setHighlightedReasonIndex(reprocessReasons.length - 1);
                      return;
                    }

                    setHighlightedReasonIndex((previousIndex) => {
                      if (previousIndex > 0) {
                        return previousIndex - 1;
                      }

                      return reprocessReasons.length - 1;
                    });

                    return;
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setReasonDropdownOpen(false);
                  }
                }}
              >
                <span
                  className={`text-[16px] leading-[100%] ${
                    selectedReason ? "text-[#191919]" : "text-[#777777]"
                  }`}
                  style={{ fontFamily: "Lufga" }}
                >
                  {selectedReason || "Select Reason"}
                </span>

                <img
                  src={downIcon}
                  alt="dropdown"
                  className="w-[16px] h-[16px] object-contain"
                />
              </div>

              {reasonDropdownOpen && (
                <div
                  className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#E0E0E0] rounded-[16px] shadow-lg overflow-hidden"
                  role="listbox"
                >
                  {reprocessReasons.map((reason, index) => (
                    <div
                      key={reason}
                      role="option"
                      aria-selected={selectedReason === reason}
                      className={`px-7 py-4 text-[18px] text-[#191919] cursor-pointer ${
                        highlightedReasonIndex === index ? "bg-[#F7F7F7]" : ""
                      }`}
                      onMouseEnter={() => setHighlightedReasonIndex(index)}
                      onClick={() => {
                        setSelectedReason(reason);
                        setReasonDropdownOpen(false);
                        setHighlightedReasonIndex(index);
                      }}
                    >
                      {reason}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-[10px]">
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                <div
                  className="w-[195px] h-[54px] flex items-center justify-center rounded-[100px] cursor-pointer"
                  style={{
                    border: "1px solid #00875A",
                    paddingTop: "16px",
                    paddingRight: "24px",
                    paddingBottom: "16px",
                    paddingLeft: "24px",
                  }}
                  onClick={() =>
                    updateStageMethods[updateStageMethodName?.method]()
                  }
                >
                  <span
                    className="text-[14px] font-medium leading-[155%] tracking-[-0.07em] text-center text-[#017C57]"
                    style={{ fontFamily: "Lufga" }}
                  >
                    Raise Reprocess Claim
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : updateStageMethodName?.method
            ?.toLowerCase()
            ?.includes("handleinitiatereprocess") ? (
          <div className="w-full h-full px-8 py-6">
            {/* Header */}
            <div className="relative flex items-center justify-center">
              <div
                className="text-[20px] font-normal leading-[100%] text-center text-[#191919]"
                style={{ fontFamily: "Lufga" }}
              >
                Update Reprocess Claim Amount
              </div>

              {/* <div
                className="absolute right-0 top-[-2px] cursor-pointer"
                onClick={() => setConfirmationPopup(false)}
              >
                <img src={CloseIcon} alt="close-icon" className="w-7 h-7" />
              </div> */}
            </div>

            {/* Divider */}
            <div
              className="w-full mt-7"
              style={{
                borderTop: "1px solid #E4E4E4",
              }}
            />

            {/* Claim Initiated Field */}
            {/* Claim Initiated + Transaction Date - Same Row */}
            <div className="flex flex-row items-center gap-[70px] mt-7">
              {/* Claim Initiated */}
              <div
                className="w-[290px] h-[52px] rounded-[100px] flex items-center"
                style={{
                  border: "1px solid #0000001A",
                  paddingTop: "20px",
                  paddingRight: "18px",
                  paddingBottom: "20px",
                  paddingLeft: "18px",
                }}
              >
                <input
                  type="number"
                  value={reprocessClaimInitiatedAmount}
                  onChange={(e) =>
                    setReprocessClaimInitiatedAmount(e.target.value)
                  }
                  placeholder="Claim Initiated"
                  className="w-full bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                  style={{
                    fontFamily: "Lufga",
                  }}
                />
              </div>

              {/* Transaction Date */}
              <div
                className="w-[290px] h-[52px] rounded-[100px] flex items-center"
                style={{
                  border: "1px solid #0000001A",
                  paddingTop: "20px",
                  paddingRight: "18px",
                  paddingBottom: "20px",
                  paddingLeft: "18px",
                }}
              >
                <span
                  className="text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777] mr-3 whitespace-nowrap"
                  style={{
                    fontFamily: "Lufga",
                  }}
                >
                  Transaction Date
                </span>

                <input
                  type="date"
                  value={reprocessTransactionDate}
                  onChange={(e) => setReprocessTransactionDate(e.target.value)}
                  className="w-[110px] bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                  style={{
                    fontFamily: "Lufga",
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-8">
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                <div
                  className="w-[180px] h-[54px] flex items-center justify-center rounded-full cursor-pointer"
                  style={{
                    border: "1px solid #017C57",
                  }}
                  onClick={() =>
                    updateStageMethods[updateStageMethodName?.method]()
                  }
                >
                  <span
                    className="text-[14px] font-medium leading-[155%] text-center text-[#017C57]"
                    style={{ fontFamily: "Lufga" }}
                  >
                    Submit
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : updateStageMethodName?.method
            ?.toLowerCase()
            ?.includes("handleinitiateerroneous") ? (
          <div
            className="w-full h-full flex flex-col"
            style={{
              padding: "30px",
              gap: "30px",
            }}
          >
            {/* Header */}
            <div className="relative flex items-center justify-center">
              <div
                className="text-[24px] font-normal leading-[100%] text-center text-[#191919]"
                style={{
                  fontFamily: "Lufga",
                }}
              >
                Update Erroneous Claim Amount
              </div>
            </div>

            {/* Divider */}
            <div
              className="w-full"
              style={{
                borderTop: "1px solid #E8E8E8",
              }}
            />

            {/* Status Fields */}
            <div className="flex flex-col gap-4 mt-7">
              {/* First Row - Claim Amount Fields */}
              <div className="flex items-center justify-between gap-[70px]">
                {/* Claim Submitted */}
                <div
                  className="w-[290px] h-[52px] flex items-center rounded-[100px]"
                  style={{
                    background: "#FFFFFF80",
                    border: "1px solid #0000001A",
                    paddingTop: "20px",
                    paddingRight: "18px",
                    paddingBottom: "20px",
                    paddingLeft: "18px",
                  }}
                >
                  <input
                    type="number"
                    value={claimSubmittedAmount}
                    onChange={(e) => setClaimSubmittedAmount(e.target.value)}
                    placeholder="Claim Submitted"
                    className="w-full bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                    style={{
                      fontFamily: "Lufga",
                    }}
                  />
                </div>

                {/* Claim Approved */}
                <div
                  className="w-[290px] h-[52px] flex items-center rounded-[100px]"
                  style={{
                    background: "#FFFFFF80",
                    border: "1px solid #0000001A",
                    paddingTop: "20px",
                    paddingRight: "18px",
                    paddingBottom: "20px",
                    paddingLeft: "18px",
                  }}
                >
                  <input
                    type="number"
                    value={claimApprovedAmount}
                    onChange={(e) => setClaimApprovedAmount(e.target.value)}
                    placeholder="Claim Approved"
                    className="w-full bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                    style={{
                      fontFamily: "Lufga",
                    }}
                  />
                </div>

                {/* Erroneous Requested */}
                <div
                  className="w-[290px] h-[52px] flex items-center rounded-[100px]"
                  style={{
                    background: "#FFFFFF80",
                    border: "1px solid #0000001A",
                    paddingTop: "20px",
                    paddingRight: "18px",
                    paddingBottom: "20px",
                    paddingLeft: "18px",
                  }}
                >
                  <input
                    type="number"
                    value={erroneousRequestedAmount}
                    onChange={(e) =>
                      setErroneousRequestedAmount(e.target.value)
                    }
                    placeholder="Erroneous Requested"
                    className="w-full bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                    style={{
                      fontFamily: "Lufga",
                    }}
                  />
                </div>
              </div>

              {/* Second Row - Transaction Date */}
              <div
                className="w-[290px] h-[52px] rounded-[100px] flex items-center"
                style={{
                  border: "1px solid #0000001A",
                  paddingTop: "20px",
                  paddingRight: "18px",
                  paddingBottom: "20px",
                  paddingLeft: "18px",
                }}
              >
                <span
                  className="text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777] mr-3 whitespace-nowrap"
                  style={{
                    fontFamily: "Lufga",
                  }}
                >
                  Transaction Date
                </span>

                <input
                  type="date"
                  value={erroneousTransactionDate}
                  onChange={(e) => setErroneousTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent outline-none border-none text-[14px] font-normal leading-[12px] tracking-[-0.03em] text-[#777777]"
                  style={{
                    fontFamily: "Lufga",
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            {/* Submit Button */}
            <div className="flex justify-center mt-5">
              {submitting ? (
                <CircularProgress size={24} />
              ) : (
                <div
                  className="w-[160px] h-[54px] flex items-center justify-center rounded-[100px] cursor-pointer"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #017C57",
                    gap: "10px",
                    paddingTop: "16px",
                    paddingRight: "24px",
                    paddingBottom: "16px",
                    paddingLeft: "24px",
                  }}
                  onClick={() =>
                    updateStageMethods[updateStageMethodName?.method]()
                  }
                >
                  <span
                    className="text-[14px] font-medium leading-[155%] tracking-[-0.07em] text-center text-[#017C57]"
                    style={{
                      fontFamily: "Lufga",
                    }}
                  >
                    Submit
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="text-xl text-center font-medium px-2">
              Are you sure you want to change current status.
            </div>

            <div className="flex justify-center items-center gap-2 mt-4">
              {submitting ? (
                <CircularProgress />
              ) : (
                <div>
                  {updateStageMethodName?.method
                    ?.toLowerCase()
                    ?.includes("handleinitiateontms") && (
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        Initiation Date on TMS
                      </span>

                      <input
                        type="datetime-local"
                        className="text-[#191919] appearance-none border border-solid border-[#017C57] bg-transparent px-5 py-4 rounded-full"
                        value={initiationDate}
                        onChange={(e) => setInitiationDate(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="flex gap-4 items-center mt-4">
                    {updateStageMethodName?.method
                      ?.toLowerCase()
                      ?.includes("handleraiseerroneousconfirmation") ||
                    updateStageMethodName?.method
                      ?.toLowerCase()
                      ?.includes("handleraisereprocessconfirmation") ? (
                      <div
                        className="w-[150px] text-white text-center bg-[#017C57] py-3 rounded-full cursor-pointer"
                        onClick={() =>
                          updateStageMethods[updateStageMethodName?.method]()
                        }
                      >
                        PROCEED
                      </div>
                    ) : (
                      <>
                        <div
                          className="w-[150px] text-white text-center bg-[#017C57] py-3 rounded-full cursor-pointer"
                          onClick={() => setConfirmationPopup(false)}
                        >
                          NO
                        </div>

                        <div
                          className="w-[150px] text-white text-center bg-[#017C57] py-3 rounded-full cursor-pointer"
                          onClick={() =>
                            updateStageMethods[updateStageMethodName?.method]()
                          }
                        >
                          YES
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CaseStageUpdateConfirmation;
